import flatten from 'lodash/flatten';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import { computed } from 'mobx';

import type { DrugFavoriteDataParams, FavoriteData } from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames } from 'chaika-enums';

import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { assertIsDefined } from '@utils/assertIsDefined';

import { store } from '@store';
import { SimpleList } from '@store/models/SimpleList';

interface FilterParams {
    groupDrugs?: boolean;
}

export class DrugFavoriteList extends SimpleList<
    FavoriteData<DrugFavoriteDataParams | DrugFavoriteDataParams[]>,
    FilterParams
> {
    @computed
    public get allDrugsIds(): string[] {
        return flatten(
            this.values
                .filter(value => isArray(value.data))
                .map(({ data }) => (isArray(data) ? data.map(({ id }) => id) : data.id)),
        );
    }

    @computed
    public get drugsUniqIds(): string[] {
        return toArrayFromIterable<string>(this.drugIdsSet);
    }

    @computed
    public get ungroupIds(): string[] {
        return this.getFilteredValuesIds({ groupDrugs: false });
    }

    @computed
    public get groupIds(): string[] {
        return this.getFilteredValuesIds({ groupDrugs: true });
    }

    public getGroupId(id: string): string {
        const groups = this.getFilteredValues({ groupDrugs: true });

        const group = groups.find(it => isArray(it.data) && it.data.some(item => item.id === id));
        assertIsDefined(group, `Drug favorite ${id} belongs to one of the favorite groups`);

        return group.id;
    }

    public getItemContent(id: string): string {
        const { name, data } = this.getExisting(id);
        let contentString;

        if (isArray(data)) {
            contentString = name;
        } else {
            const { activeSubstance, directions, frequencyIfReception } = data;
            const dictionary = store.dictionary.getDictionary(DictionaryNames.ElementDrug);

            contentString = `${(activeSubstance && dictionary.get(activeSubstance)?.title) || ''} ${
                frequencyIfReception || ''
            } ${directions || ''} `;
        }
        return contentString;
    }

    public getItemTitle(id: string): string {
        const { name, data } = this.getExisting(id);
        let title;

        if (isArray(data)) {
            title = name;
        } else {
            const { tradeName } = data;
            const dictionary = store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);

            title = (tradeName && dictionary.get(tradeName)?.title) || '';
        }

        return title;
    }

    public getItemTradeNames(tradeNameIds: string[]): string[] {
        const dictionary = store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);

        return tradeNameIds.map(id => dictionary.get(id)?.title ?? '');
    }

    public getItemActiveSubstance(activeSubstance: string): string {
        const dictionary = store.dictionary.getDictionary(DictionaryNames.ElementDrug);

        return dictionary.get(activeSubstance)?.title ?? '-';
    }

    public getGroupDrugsCount(groupId: string): number {
        return this.getGroupDrugsIds(groupId).length;
    }

    public getGroupDrugsFavoriteData(groupId: string): DrugFavoriteDataParams[] {
        const group = this.getExisting(groupId);

        return group.data as DrugFavoriteDataParams[];
    }

    public getGroupDrugFavoriteData(groupId: string, id: string): DrugFavoriteDataParams {
        const { data } = this.getExisting(groupId);

        const favoriteData = (data as DrugFavoriteDataParams[]).find(params => params.id === id);
        assertIsDefined(favoriteData, `Drug favorite ${id} belongs to the favorite group ${groupId}`);
        return favoriteData;
    }

    public getGroupDrugsIds(groupId: string): string[] {
        const { data } = this.getExisting(groupId);
        const ids = [];

        if (isArray(data)) {
            ids.push(...data.map(({ id }) => id));
        }

        return ids;
    }

    public getRelatedGroupIds(drugId: string) {
        return this.values
            .filter(group => isArray(group.data) && group.data.some(item => item.id === drugId))
            .map(({ id }) => id);
    }

    protected filterValue(
        { data }: FavoriteData<DrugFavoriteDataParams | DrugFavoriteDataParams[]>,
        { groupDrugs }: FilterParams,
    ): boolean {
        return !isNil(groupDrugs) ? (groupDrugs ? isArray(data) : !isArray(data)) : true;
    }

    @computed
    private get drugIdsSet(): Set<string> {
        return new Set(
            flatten(
                this.values
                    .filter(value => isArray(value.data))
                    .map(({ data }) => (isArray(data) ? data.map(({ id }) => id) : data.id)),
            ),
        );
    }
}
